// Free credit from the team (Brúnó, 2026-10-14: "amikor megnyitja a brand a
// felületét legyen modal róla meg animáció, hogy feltöltődik"). The first time
// an Admin opens the workspace after a gift, coins drop into the wallet, the
// amount counts up to what was given and the balance to what it is now, with
// the team's message below. Closing it, or following its link, marks it seen
// for the whole team. With reduced motion everything stands still at its end.

import {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import type {BrandCreditGift} from '../../shared/credit-gifts';
import {Icon} from '../creator/components/Icon';
import {markGiftsSeen} from './api';
import {useBrand} from './store';
import './credit-gift.css';

/** When the coins have landed and the numbers start, in ms. */
const COUNT_DELAY = 950;
const COUNT_MS = 1100;

/** Dollars, with cents only when there are some: "$500", "$1,479.26". */
function usd(cents: number): string {
  return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2}).format(cents / 100);
}

function reducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * A number of cents that runs from `from` to `to` once, easing out, in whole
 * dollars on the way (cents flickering by read as noise) and exact at the end;
 * at once with reduced motion.
 */
function useCountUp(from: number, to: number, delay: number): number {
  const still = useRef(reducedMotion());
  const [value, setValue] = useState(still.current ? to : from);
  useEffect(() => {
    if (still.current) { setValue(to); return undefined; }
    let frame = 0;
    const start = performance.now() + delay;
    const tick = (time: number) => {
      const t = Math.min(1, Math.max(0, (time - start) / COUNT_MS));
      setValue(t >= 1 ? to : Math.round((from + (to - from) * (1 - (1 - t) ** 3)) / 100) * 100);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [from, to, delay]);
  return value;
}

/** The wallet the coins fall into, with the light behind it centred on it. */
function WalletArt() {
  return (
    <div className="gift-art" aria-hidden="true">
      <span className="gift-glow" />
      <svg className="gift-rays" viewBox="-100 -100 200 200">
        {Array.from({length: 12}, (_, index) => <path key={index} d="M-5 -96 L5 -96 L1.5 -40 L-1.5 -40 Z" transform={`rotate(${index * 30})`} />)}
      </svg>
      <svg className="gift-sparks" viewBox="-100 -100 200 200">
        {Array.from({length: 8}, (_, index) => <line key={index} x1="0" y1="-52" x2="0" y2="-66" transform={`rotate(${index * 45 + 22.5})`} />)}
      </svg>
      <span className="gift-coin c1" /><span className="gift-coin c2" /><span className="gift-coin c3" />
      <span className="gift-shadow" />
      <svg className="gift-wallet" viewBox="0 0 96 80">
        <rect x="10" y="10" width="76" height="40" rx="9" className="wallet-back" />
        <rect x="18" y="4" width="54" height="30" rx="5" className="wallet-bill" transform="rotate(-8 45 19)" />
        <rect x="26" y="10" width="54" height="30" rx="5" className="wallet-bill b2" transform="rotate(5 53 25)" />
        <path d="M6 30c0-6.6 5.4-12 12-12h60c6.6 0 12 5.4 12 12v32c0 6.6-5.4 12-12 12H18c-6.6 0-12-5.4-12-12z" className="wallet-front" />
        <path d="M6 30c0-6.6 5.4-12 12-12h60c6.6 0 12 5.4 12 12v3H6z" className="wallet-lip" />
        <rect x="58" y="38" width="32" height="20" rx="10" className="wallet-clasp" />
        <circle cx="70" cy="48" r="4" className="wallet-dot" />
      </svg>
    </div>
  );
}

export function CreditGiftDialog({gifts}: {gifts: BrandCreditGift[]}) {
  const {data, reload} = useBrand();
  const [closing, setClosing] = useState(false);
  // Closed here at once; a failed save only means it shows again next time.
  const [hidden, setHidden] = useState(false);
  const total = gifts.reduce((sum, gift) => sum + gift.amount_cents, 0);
  const balance = data?.billing.balance_cents ?? total;
  const shownTotal = useCountUp(0, total, COUNT_DELAY);
  const shownBalance = useCountUp(balance - total, balance, COUNT_DELAY + 150);
  const first = gifts[0]!;
  const title = gifts.length === 1 ? first.title : 'Credit from Earn';
  const messages = gifts.filter((gift) => gift.message.trim());

  async function close() {
    if (closing) return;
    setClosing(true);
    window.setTimeout(() => setHidden(true), 200);
    try { await markGiftsSeen(gifts.map((gift) => gift.id)); } catch { /* shown again next time: harmless */ }
    await reload(true);
  }
  // Escape closes it; bound once, through a ref, rather than on every frame of the count.
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') void closeRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const started = (data?.campaigns.length ?? 0) > 0;
  if (hidden) return null;
  return <div className={`gift-layer${closing ? ' closing' : ''}`}>
    <div className="gift-backdrop" onClick={() => void close()} />
    <div className="gift-card" role="dialog" aria-modal="true" aria-labelledby="gift-title" aria-describedby="gift-amount">
      <button className="switch-close" type="button" onClick={() => void close()} aria-label="Close"><Icon name="x" size={18} /></button>
      <WalletArt />
      <p className="eyebrow">Credit added</p>
      <h2 id="gift-title">{title}</h2>
      <p className="gift-amount" id="gift-amount" aria-label={`${usd(total)} of credit added`}>+{usd(shownTotal)}</p>
      <p className="gift-balance">Your credit is now <b>{usd(shownBalance)}</b></p>
      {messages.length ? <div className="gift-messages">{messages.map((gift) => <blockquote key={gift.id}>
        {gifts.length > 1 ? <b>{gift.title} · {usd(gift.amount_cents)}</b> : null}
        <p>{gift.message}</p>
      </blockquote>)}<span className="gift-from">The Earn team</span></div> : null}
      <div className="gift-actions">
        <Link className="btn btn-g" to={started ? '/campaigns' : '/campaigns/new'} onClick={() => void close()}>
          {started ? 'Bring creators in' : 'Start a campaign'}<Icon name="arrowRight" size={16} /></Link>
        <button className="btn btn-light" type="button" onClick={() => void close()}>Got it</button>
      </div>
    </div>
  </div>;
}
